import React, { useCallback, useEffect, useState } from 'react';
import MainLayout from '../layouts/MainLayout';
import { 
    Box, 
    Button, 
    CircularProgress, 
    Typography,
    Card,
    Alert,
    TextField,
    debounce,
    ListItemText,
    MenuItem,
    ListItemIcon,
    Menu
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {  downloadFilteredDpes, getDpes, searchDpesByNumeroDPE } from '../services/dpeService';
import { DpeComplet } from '../interfaces/DpeComplet';
import DpeTable from '../components/DpeTable';
import DpeModal from '../components/DpeModal';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const DpeManagement: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDpe, setSelectedDpe] = useState<DpeComplet | null>(null);
    const [dpes, setDpes] = useState<DpeComplet[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [numeroDPE, setNumeroDPE] = useState<string>('');
    const [logiciel, setLogiciel] = useState<string>('');
    const [version, setVersion] = useState<string>('');
    const [typeBatiment, setTypeBatiment] = useState<string>('');
    const [zoneClimatique, setZoneClimatique] = useState<string>('');
    const [classeAltitude, setClasseAltitude] = useState<string>('');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);


    const debouncedSearch = useCallback(
        debounce((numeroDPE: string, logiciel: string, version: string, typeBatiment: string, zoneClimatique: string, classeAltitude: string) => {
            setPage(0); 
            fetchDpes(numeroDPE, logiciel, version, typeBatiment, zoneClimatique, classeAltitude);
        }, 500),
        []
    );

    const fetchDpes = useCallback(async (numeroDPE: string = '', logiciel: string = '', version: string = '', typeBatiment: string = '', zoneClimatique: string = '', classeAltitude: string = '') => {
        try {
            setLoading(true);
            setError(null);

            const response = (numeroDPE || logiciel || version || typeBatiment || zoneClimatique || classeAltitude)
            ? await searchDpesByNumeroDPE(numeroDPE,logiciel,version,typeBatiment,zoneClimatique,classeAltitude, page, rowsPerPage)
            : await getDpes({ 
                page: page,
                size: rowsPerPage 
            });

            if (response && response.content) {
                setDpes(response.content);
                setTotalElements(response.totalElements || 0);
            } else {
                console.error('Format de réponse inattendu:', response);
                setDpes([]);
                setTotalElements(0);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des DPEs:', error);
            setDpes([]);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage]);

    useEffect(() => {
        fetchDpes(numeroDPE, logiciel, version, typeBatiment, zoneClimatique, classeAltitude);
    }, [fetchDpes, page, rowsPerPage]);

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0); // Reset to first page when changing rows per page
    };

    // Gestionnaire pour le rafraîchissement manuel
    const handleRefresh = async () => {
        await fetchDpes();
    };

    const handleEditDpe = (dpe: DpeComplet) => {
        setSelectedDpe(dpe);
        setIsModalOpen(true);
    };

    const handleCreateDpe = () => {
        setSelectedDpe(null);
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        setIsModalOpen(false);
        handleRefresh();
    }

    const handleSearchChange = (numeroDPE: string, logiciel: string, version: string, typeBatiment: string, zoneClimatique: string, classeAltitude: string) => {
        setNumeroDPE(numeroDPE);
        setLogiciel(logiciel);
        setVersion(version);
        setTypeBatiment(typeBatiment);
        setZoneClimatique(zoneClimatique);
        setClasseAltitude(classeAltitude);
        debouncedSearch(numeroDPE, logiciel, version, typeBatiment, zoneClimatique, classeAltitude);
    };

    const handleClickBulkEdit = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseBulkEdit = () => {
        setAnchorEl(null);
    };

    const handleDownloadAll = async () => {
        try {
            await downloadFilteredDpes('', '', '', '', '', '');
            handleCloseBulkEdit();
        } catch (error) {
            console.error('Erreur lors du téléchargement de tous les DPEs:', error);
        }
    };

    const handleDownloadFiltered = async () => {
        try {
            await downloadFilteredDpes(numeroDPE, logiciel, version, typeBatiment, zoneClimatique, classeAltitude);
            handleCloseBulkEdit();
        } catch (error) {
            console.error('Erreur lors du téléchargement des DPEs filtrés:', error);
        }
    };

    /*
    const handleExcelExport = async (dpeId: string) => {
        try {
            await downloadDpeExcel(dpeId);
        } catch (error) {
            console.error('Erreur lors du téléchargement du fichier Excel:', error);
            // Optionnel : Ajouter une notification d'erreur
        }
    };

     */

    return (
        <MainLayout>
            <Box className="p-6 space-y-6">
                {/* En-tête */}
                <Card className="bg-white/80 backdrop-blur-sm p-6">
                    <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <Typography variant="h4" className="text-emerald-800 font-bold mb-2">
                                Gestion des DPEs
                            </Typography>
                            <Typography variant="body1" className="text-emerald-600">
                                Gérez et suivez les diagnostics de performance énergétique
                            </Typography>
                        </div>
                        <Button
                            variant="contained"
                            onClick={handleCreateDpe}
                            startIcon={<AddIcon />}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                            Ajouter un DPE
                        </Button>
                    </Box>
                </Card>

                {error && (
                    <Alert 
                        severity="error" 
                        onClose={() => setError(null)}
                        className="rounded-lg"
                    >
                        {error}
                    </Alert>
                )}

                <Card className="bg-white/80 backdrop-blur-sm p-2 w-full" sx={{ columns: 3 }}>
                    <Box className="flex-1 items-center" >
                        <TextField
                            fullWidth                            
                            label="Rechercher par numéro DPE"
                            variant="outlined"
                            value={numeroDPE}
                            onChange={(e) => handleSearchChange(e.target.value, logiciel, version, typeBatiment, zoneClimatique, classeAltitude)}
                            size="small"                            
                            className="bg-white"
                        />
                        <TextField
                            fullWidth
                            label="Rechercher par logiciel"
                            variant="outlined"
                            value={logiciel}
                            onChange={(e) => handleSearchChange(numeroDPE, e.target.value, version, typeBatiment, zoneClimatique, classeAltitude)}
                            size="small"
                            sx={{ marginTop: 2 }}
                            className=" bg-white"
                        />
                        <TextField
                            fullWidth
                            label="Rechercher par version"
                            variant="outlined"
                            value={version}
                            onChange={(e) => handleSearchChange(numeroDPE, logiciel, e.target.value, typeBatiment, zoneClimatique, classeAltitude)}
                            size="small"
                            className="max-w-md bg-white"
                        />
                        <TextField
                            fullWidth
                            label="Rechercher par type de bâtiment"
                            variant="outlined"
                            value={typeBatiment}
                            onChange={(e) => handleSearchChange(numeroDPE, logiciel, version, e.target.value, zoneClimatique, classeAltitude)}
                            size="small"
                            sx={{ marginTop: 2 }}
                            className="max-w-md bg-white"
                        />
                        <TextField
                            fullWidth
                            label="Rechercher par zone climatique"
                            variant="outlined"
                            value={zoneClimatique}
                            onChange={(e) => handleSearchChange(numeroDPE, logiciel, version, typeBatiment, e.target.value, classeAltitude)}
                            size="small"
                            className="max-w-md bg-white"
                        />
                        <TextField
                            fullWidth
                            label="Rechercher par altitude"
                            variant="outlined"
                            value={classeAltitude}
                            onChange={(e) => handleSearchChange(numeroDPE, logiciel, version, typeBatiment, zoneClimatique, e.target.value)}
                            size="small"
                            sx={{ marginTop: 2 }}
                            className="max-w-md bg-white"
                        />                        
                        {(numeroDPE || logiciel || version || typeBatiment || zoneClimatique || classeAltitude) && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                    setNumeroDPE('');
                                    setLogiciel('');
                                    setVersion('');
                                    setTypeBatiment('');
                                    setZoneClimatique('');
                                    setClasseAltitude('');
                                    fetchDpes('', '', '', '', '', '');
                                }}
                                className="text-gray-600 border-gray-300"
                            >
                                Réinitialiser
                            </Button>
                        )}
                    </Box>

                </Card>

                <Card className="bg-white/80 backdrop-blur-sm p-2 w-full " >
                    <Box className="flex justify-end">
                        <Button
                            variant="contained"
                            onClick={handleClickBulkEdit}
                            endIcon={<KeyboardArrowDownIcon />}
                            className="bg-emerald-600 hover:bg-emerald-700"
                        >
                            Téléchargement groupé
                        </Button>
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleCloseBulkEdit}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        >
                            <MenuItem onClick={handleDownloadAll}>
                                <ListItemIcon>
                                    <DownloadIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Télécharger tous les DPEs</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={handleDownloadFiltered}>
                                <ListItemIcon>
                                    <FilterListIcon  fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Télécharger les DPEs filtrés</ListItemText>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Card>

                {/* Table des DPEs */}
                <Card className="bg-white/80 backdrop-blur-sm overflow-hidden">
                    {loading ? (
                        <Box className="flex justify-center items-center p-12">
                            <CircularProgress className="text-emerald-600" />
                        </Box>
                    ) : (
                        <Box className="overflow-x-auto">                            
                            <DpeTable
                                dpes={dpes}
                                onEditDpe={handleEditDpe}
                                refreshDpes={() => fetchDpes(numeroDPE, logiciel, version, typeBatiment, zoneClimatique, classeAltitude)}                  
                                totalElements={totalElements}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                onPageChange={handlePageChange}
                                onRowsPerPageChange={handleRowsPerPageChange}
                                numeroDPE={numeroDPE}
                                logiciel={logiciel}
                                version={version}
                                typeBatiment={typeBatiment}
                                zoneClimatique={zoneClimatique}
                                classeAltitude={classeAltitude}
                            />                                                        
                        </Box>
                    )}
                </Card>

                {/* Modal de création/édition */}
                <DpeModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSave}
                    dpe={selectedDpe}
                    refreshDpes={fetchDpes}
                />
            </Box>
        </MainLayout>
    );
};

export default DpeManagement;